import matches from 'validator/lib/matches';
import { createField, updateField } from '../../actions';
import { EVENT_STEP_VALIDATE, POSTAL_CODE_NL, POSTAL_CODE_BE, POSTAL_CODE_OTHER } from '../../helpers/consts';

export class PostalCodeField {
    constructor(options) {
        this.validateMessages = {
            isEmpty: __('Please enter your zip code'),
            noPostalCode: __('This is not a correct zip code'),
        };
        this.store = options.store;
        this.state = this.store.getState();
        this.$context = options.$context;
        this.id = options.id;
        this.label = options.label;
        this.required = options.required !== undefined ? options.required : true;
        this.stepId = options.stepId
        const isOther = !(options.mode === POSTAL_CODE_NL || options.mode === POSTAL_CODE_BE);
        this.mode = isOther ? POSTAL_CODE_OTHER : options.mode;
        const fieldState = this.state.formFields.find(field => field.id === this.id );
        this.value = (fieldState && fieldState.value) ? fieldState.value : '';
        this.isValid = false;
        this.$el = this.createFieldElement();

        if (this.value !== '' || !this.required) {
            this.validate();
        }

        this.setMode(this.mode);
    }

    createFieldElement () { // eslint-disable-line class-methods-use-this
        let debounceTimeout;
        const $field = document.createElement('div');
        const $combinedField = document.createElement('div');
        const $combinedFieldFirst = document.createElement('div');
        const $combinedFieldSecond = document.createElement('div');
        const $fieldLabel = document.createElement('label');
        this.$fieldNumberInput = document.createElement('input');
        this.$fieldLetterInput = document.createElement('input');
        this.$fieldMessage = document.createElement('span');

        // Number part of the postal code
        this.$fieldNumberInput.type = 'text';
        this.$fieldNumberInput.required = this.required;
        this.$fieldNumberInput.placeholder = '1234';
        this.$fieldNumberInput.minLength = 4;
        this.$fieldNumberInput.maxLength = 4;
        this.$fieldNumberInput.id = `${this.id}--number`;
        this.$fieldNumberInput.value = this.value ? Number(this.value.substr(0, 4)) : '';

        // Letter part of the postal code
        this.$fieldLetterInput.type = 'text';
        this.$fieldLetterInput.placeholder = 'AB';
        this.$fieldLetterInput.minLength = 2;
        this.$fieldLetterInput.maxLength = 2;
        this.$fieldLetterInput.id = `${this.id}--letter`;
        this.$fieldLetterInput.value = this.value ? this.value.substr(4, 2) : '';

        $fieldLabel.setAttribute('for', `${this.id}--number`);
        $fieldLabel.innerHTML = this.label;

        $field.classList.add('field', 'form-group', 'field--postal-code');
        $combinedField.classList.add('row');
        $combinedFieldFirst.classList.add('col-6');
        $combinedFieldSecond.classList.add('col-6');
        $fieldLabel.classList.add('field-label');
        this.$fieldNumberInput.classList.add('field-input', 'form-control', 'field-input--postal-code-number');
        this.$fieldLetterInput.classList.add('field-input', 'form-control', 'field-input--postal-code-letter');
        this.$fieldMessage.classList.add('invalid-feedback');
        this.$fieldMessage.setAttribute('role', 'alert');

        $field.appendChild($fieldLabel);
        $combinedFieldFirst.appendChild(this.$fieldNumberInput);
        $combinedFieldSecond.appendChild(this.$fieldLetterInput);
        $combinedField.append($combinedFieldFirst);
        $combinedField.append($combinedFieldSecond);
        $field.appendChild($combinedField);
        $field.appendChild(this.$fieldMessage);

        this.store.dispatch(createField(this.id, this.label, this.stepId));
        this.$fieldNumberInput.addEventListener('blur', this.validate.bind(this));
        this.$fieldNumberInput.addEventListener('input', () => {
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(this.validate.bind(this), 100);
        });

        this.$fieldLetterInput.addEventListener('input', () => {
            this.$fieldLetterInput.value = this.$fieldLetterInput.value.replace(/[^a-zA-Z]/, '');
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(this.validate.bind(this), 100);
        });

        return $field;
    }

    getValue() {
        return this.$fieldNumberInput.value + this.$fieldLetterInput.value.toUpperCase();
    }

    setValue() {
        this.$fieldNumberInput.value = ''
        this.$fieldLetterInput.value = '';
        this.validate();
    }

    setMode(mode) {
        this.mode = mode;

        this.$el.classList.toggle('field--single', this.mode !== POSTAL_CODE_NL);
        this.$fieldLetterInput.classList.toggle('visually-hidden', this.mode !== POSTAL_CODE_NL);

        if (this.mode === POSTAL_CODE_NL || this.mode === POSTAL_CODE_BE) {
            this.$fieldNumberInput.value = this.$fieldNumberInput.value.replace(/[^0-9]/g, '').substr(0,4);
        }

        this.$fieldNumberInput.maxLength = 4;
        if (this.mode === POSTAL_CODE_OTHER) {
            this.$fieldNumberInput.removeAttribute('maxLength');
        }
    }

    validate() {
        this.$fieldNumberInput.classList.remove('is-invalid');
        this.$fieldNumberInput.classList.remove('is-valid');
        this.isValid = false;

        if (this.mode !== POSTAL_CODE_OTHER) {
            this.$fieldNumberInput.value = this.$fieldNumberInput.value.replace(/[^0-9]/, '');
        } else {
            this.$fieldNumberInput.value = this.$fieldNumberInput.value.replace(/[^0-9a-zA-Z\s]/, '');
        }

        if (this.mode === POSTAL_CODE_NL && this.$fieldNumberInput.value.length === 4) {
            this.$fieldLetterInput.focus();
        }

        let regex = /^[1-9][0-9]{3} ?((?!sa|sd|ss)[a-z]{2})$/i;
        let postalCodeValue = this.getValue();

        if (this.mode === POSTAL_CODE_NL) {
            if(postalCodeValue.length === 4) {
                return;
            }
        }

        if (this.mode === POSTAL_CODE_BE) {
            regex = /^[1-9][0-9]{3}$/i;
            postalCodeValue = this.$fieldNumberInput.value;
        }

        if (this.mode === POSTAL_CODE_OTHER) {
            regex = /^[a-zA-Z0-9\s]+$/i;
            postalCodeValue = this.$fieldNumberInput.value;
        }

        if (this.$fieldNumberInput.value === '') {
            if (this.required) {
                this.$fieldNumberInput.classList.add('is-invalid');
                this.isValid = false;
                this.$fieldMessage.innerHTML = this.validateMessages.isEmpty.replace(/%s/g, this.label);
            }

            this.$context.dispatchEvent(EVENT_STEP_VALIDATE);
            return;
        }

        if(matches(postalCodeValue, regex)) {
            // Valid postalcode
            this.$fieldNumberInput.classList.remove('is-invalid');
            this.$fieldNumberInput.classList.add('is-valid');
            this.isValid = true;
        } else {
            // Not valid postal code
            this.$fieldNumberInput.classList.add('is-invalid');
            this.$fieldMessage.innerHTML = this.validateMessages.noPostalCode;

            return;
        }

        if (this.isValid) {
            this.store.dispatch(updateField(this.id, postalCodeValue, this.isValid, this.label));
        }
        this.$context.dispatchEvent(EVENT_STEP_VALIDATE);
    }

    valid() {
        return this.isValid;
    }

    render() {
        return this.$el;
    }
}

export default PostalCodeField;
